/**
 * Resolve the revealed amount for a queued SRC-20 transfer on the details path.
 *
 * Returns `{ value, decimals }` for the shared `TokenAmount` renderer, or `null`
 * to keep the amount masked. The reveal decision itself lives in the pure
 * `resolveRevealedSrc20Amount`; this hook only gathers its inputs (owner status,
 * the token's real decimals, queued status, the displayed recipient). Real
 * decimals come from the SRC-20 token list — never the CGW's `decimals: 0`.
 */
import { useMemo } from 'react'
import type { Transaction, TransferTransactionInfo } from '@safe-global/store/gateway/AUTO_GENERATED/transactions'
import { sameAddress } from '@safe-global/utils/utils/addresses'
import { isSrc20Transfer, isTxQueued } from '@/utils/transaction-guards'
import { useIsSafeOwnerOnSeismic } from './useIsSafeOwnerOnSeismic'
import { useSrc20TokenList } from './useSrc20TokenList'
import { resolveRevealedSrc20Amount, type RevealedSrc20Amount } from '../services/revealSrc20Amount'

export const useRevealedSrc20Amount = (
  txInfo: TransferTransactionInfo,
  hexData: string | undefined | null,
  txStatus: Transaction['txStatus'],
): RevealedSrc20Amount | null => {
  const isOwner = useIsSafeOwnerOnSeismic()
  const transfer = txInfo.transferInfo
  const queued = isTxQueued(txStatus)
  // Only fetch the token list when a reveal is actually possible — avoids an
  // explorer fetch for native/ERC20/ERC721 details or executed transfers.
  const canReveal = isOwner && queued && isSrc20Transfer(transfer)
  const { tokens } = useSrc20TokenList({ enabled: canReveal })

  return useMemo(() => {
    if (!isSrc20Transfer(transfer)) return null
    const token = tokens.find((t) => sameAddress(t.address, transfer.tokenAddress))
    return resolveRevealedSrc20Amount({
      hexData,
      decimals: token?.decimals,
      isOwner,
      isQueued: queued,
      expectedRecipient: txInfo.recipient.value,
    })
  }, [transfer, hexData, tokens, isOwner, queued, txInfo.recipient.value])
}

export default useRevealedSrc20Amount
